import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Loader2, AlertCircle, CheckCircle, Save, Lightbulb, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { itemsService } from '../../../../services/itemsService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { pickRuleService } from '../../../../services/pickRuleService';
import { stcSortingBoxDefService, StcSortingBoxDefDto } from '../../../../services/stcSortingBoxDefService';
import { stcBinSizeService } from '../../../../services/stcBinSizeService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../services/stcMovementTypeService';
import { AdvancedItemConfigDto, UpdateAdvancedItemConfigRequest } from '../../../../types/advancedItemConfig';
import { ItemBinSizeDto } from '../../../../types/itemBinSize';
import { PickRuleDto } from '../../../../types/pickRule';
import { StcBinSizeDto } from '../../../../types/stcBinSize';

interface AdvancedItemConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyNo: string;
  itemNo: string;
  onSave?: () => void;
  /**
   * When set, the modal renders a yellow banner explaining that goods receipt
   * cannot continue until master data is registered. Used by the receipt module
   * to enforce master-data validation in advanced warehouses (UseAWYesNo = 1).
   */
  receivingValidation?: {
    binGrpNoMissing: boolean;
    itemBinSizeMissing: boolean;
  };
}

interface ItemBinSizeEditRow {
  id: number;
  binSizeNo: number | null;
  maxQuantity: number;
  putAwayPriorityNo: number;
  isNew: boolean;
}

export const AdvancedItemConfigModal: React.FC<AdvancedItemConfigModalProps> = ({
  isOpen,
  onClose,
  companyNo,
  itemNo,
  onSave,
  receivingValidation
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form data
  const [binGrpNo, setBinGrpNo] = useState<number | null>(null);
  const [pickRuleNo, setPickRuleNo] = useState<number | null>(null);
  const [stcBoxTypeID, setStcBoxTypeID] = useState<string>('');
  const [movementTypeNo, setMovementTypeNo] = useState<number | null>(null);
  const [dedicatedBinCode, setDedicatedBinCode] = useState<string>('');
  const [putAwayToPickableBinYesNo, setPutAwayToPickableBinYesNo] = useState<boolean>(false);
  const [stdBinCode, setStdBinCode] = useState<string>('');
  const [useSharedBinYesNo, setUseSharedBinYesNo] = useState<boolean>(false);
  const [itemBinSizes, setItemBinSizes] = useState<ItemBinSizeEditRow[]>([]);

  // Dropdown options
  const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
  const [pickRules, setPickRules] = useState<PickRuleDto[]>([]);
  const [sortingBoxTypes, setSortingBoxTypes] = useState<StcSortingBoxDefDto[]>([]);
  const [movementTypes, setMovementTypes] = useState<StcMovementTypeDto[]>([]);
  const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAllData();
    } else {
      // Reset state when modal closes
      resetForm();
    }
  }, [isOpen, companyNo, itemNo]);

  const resetForm = () => {
    setError(null);
    setSuccessMessage(null);
    setBinGrpNo(null);
    setPickRuleNo(null);
    setStcBoxTypeID('');
    setMovementTypeNo(null);
    setDedicatedBinCode('');
    setPutAwayToPickableBinYesNo(false);
    setStdBinCode('');
    setUseSharedBinYesNo(false);
    setItemBinSizes([]);
  };

  const loadAllData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      // Load all dropdown options in parallel
      const [binGrpsData, pickRulesData, sortingBoxTypesData, movementTypesData, binSizesData, configData] = await Promise.all([
        stcBinGrpService.getCurrent(),
        pickRuleService.getAll(),
        stcSortingBoxDefService.getCurrent(),
        stcMovementTypeService.getCurrent(),
        stcBinSizeService.getCurrent(),
        itemsService.getAdvancedConfig(companyNo, itemNo)
      ]);

      setBinGroups(binGrpsData);
      setPickRules(pickRulesData);
      setSortingBoxTypes(sortingBoxTypesData);
      setMovementTypes(movementTypesData);
      setBinSizes(binSizesData);

      // Populate form with existing data if available
      if (configData) {
        setBinGrpNo(configData.binGrpNo ?? null);
        setPickRuleNo(configData.pickRuleNo ?? null);
        setStcBoxTypeID(configData.stcBoxTypeID ?? '');
        setMovementTypeNo(configData.movementTypeNo ?? null);
        setDedicatedBinCode(configData.dedicatedBinCode ?? '');
        setPutAwayToPickableBinYesNo(configData.putAwayToPickableBinYesNo ?? false);
        setStdBinCode(configData.stdBinCode ?? '');
        setUseSharedBinYesNo(configData.useSharedBinYesNo ?? false);

        // Convert ItemBinSizeDto to edit rows
        setItemBinSizes(
          configData.itemBinSizes.map(ibs => ({
            id: ibs.id,
            binSizeNo: ibs.binSizeNo,
            maxQuantity: ibs.maxQuantity,
            putAwayPriorityNo: ibs.putAwayPriorityNo,
            isNew: false
          }))
        );
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Kunne ikke hente data: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddBinSize = () => {
    const newRow: ItemBinSizeEditRow = {
      id: -(Date.now()), // Temporary negative ID for new rows
      binSizeNo: null,
      maxQuantity: 0,
      putAwayPriorityNo: 1,
      isNew: true
    };
    setItemBinSizes([...itemBinSizes, newRow]);
  };

  const handleRemoveBinSize = (id: number) => {
    setItemBinSizes(itemBinSizes.filter(row => row.id !== id));
  };

  const handleBinSizeChange = (id: number, field: keyof ItemBinSizeEditRow, value: any) => {
    setItemBinSizes(
      itemBinSizes.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const validateForm = (): string | null => {
    // Check for duplicate BinSizeNo
    const binSizeNos = itemBinSizes
      .filter(row => row.binSizeNo !== null)
      .map(row => row.binSizeNo);

    const duplicates = binSizeNos.filter((item, index) => binSizeNos.indexOf(item) !== index);
    if (duplicates.length > 0) {
      return `Bin-størrelse ${duplicates[0]} er brukt flere ganger. Hver bin-størrelse kan bare brukes én gang.`;
    }

    // Check for rows with missing BinSizeNo
    const rowsWithoutBinSize = itemBinSizes.filter(row => row.binSizeNo === null);
    if (rowsWithoutBinSize.length > 0) {
      return 'Alle bin-størrelser må ha en valgt bin-størrelse.';
    }

    // Validate positive numbers (allow 0 for maxQuantity - means no placement allowed)
    for (const row of itemBinSizes) {
      if (row.maxQuantity < 0) {
        return 'Maks antall kan ikke være negativt.';
      }
      if (row.putAwayPriorityNo <= 0) {
        return 'Plasseringsprioritet må være et positivt tall.';
      }
    }

    return null;
  };

  const handleSuggestBin = async () => {
    try {
      setLoadingSuggestion(true);
      setError(null);
      setSuccessMessage(null);

      const result = await itemsService.suggestStandardBin(companyNo, itemNo);

      if (result.success && result.suggestedBinCode) {
        setStdBinCode(result.suggestedBinCode);
        setSuccessMessage(result.message || 'Lokasjon foreslått');
      } else {
        setError(result.message || 'Kunne ikke finne passende lokasjon');
      }
    } catch (err) {
      console.error('Error suggesting bin:', err);
      setError(`Kunne ikke foreslå lokasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSuggestDedicatedBin = async () => {
    try {
      setLoadingSuggestion(true);
      setError(null);
      setSuccessMessage(null);

      const result = await itemsService.suggestDedicatedBin(companyNo, itemNo);

      if (result.success && result.suggestedBinCode) {
        setDedicatedBinCode(result.suggestedBinCode);
        setSuccessMessage(result.message || 'Dedikert lokasjon foreslått');
      } else {
        setError(result.message || 'Kunne ikke finne ledig lokasjon');
      }
    } catch (err) {
      console.error('Error suggesting dedicated bin:', err);
      setError(`Kunne ikke foreslå dedikert lokasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      // Build update request
      const request: UpdateAdvancedItemConfigRequest = {
        binGrpNo: binGrpNo ?? undefined,
        pickRuleNo: pickRuleNo ?? undefined,
        stcBoxTypeID: stcBoxTypeID || undefined,
        movementTypeNo: movementTypeNo ?? undefined,
        dedicatedBinCode: dedicatedBinCode || undefined,
        putAwayToPickableBinYesNo: putAwayToPickableBinYesNo,
        stdBinCode: stdBinCode || undefined,
        useSharedBinYesNo: useSharedBinYesNo,
        itemBinSizes: itemBinSizes.map(row => ({
          id: row.isNew ? 0 : row.id, // Backend will insert if id=0
          companyNo,
          itemNo,
          binSizeNo: row.binSizeNo!,
          maxQuantity: row.maxQuantity,
          putAwayPriorityNo: row.putAwayPriorityNo
        }))
      };

      await itemsService.updateAdvancedConfig(companyNo, itemNo, request);

      setSuccessMessage('Grunndata lagret');

      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }

      // Close modal after short delay to show success message
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('Error saving advanced config:', err);
      setError(`Kunne ikke lagre: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getBinSizeDescription = (binSizeNo: number): string => {
    const binSize = binSizes.find(bs => bs.binSizeNo === binSizeNo);
    return binSize?.description1 || '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-5xl sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
            Grunndata - {itemNo}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
          {/* Receiving validation banner — shown when modal is opened because master data is missing */}
          {receivingValidation && (receivingValidation.binGrpNoMissing || receivingValidation.itemBinSizeMissing) && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800">
                    {t(
                      'warehouse.modules.receipt.masterDataValidation.title',
                      {},
                      'Grunndata mangler for varemottak'
                    )}
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {t(
                      'warehouse.modules.receipt.masterDataValidation.description',
                      {},
                      'Denne varen mangler nødvendig grunndata for å kunne mottas i et avansert lager. Fyll ut feltene under og lagre for å fortsette mottaket.'
                    )}
                  </p>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-0.5">
                    {receivingValidation.binGrpNoMissing && (
                      <li>
                        {t(
                          'warehouse.modules.receipt.masterDataValidation.missingBinGrp',
                          {},
                          'Lokasjonsgruppe (BinGrpNo) er ikke satt'
                        )}
                      </li>
                    )}
                    {receivingValidation.itemBinSizeMissing && (
                      <li>
                        {t(
                          'warehouse.modules.receipt.masterDataValidation.missingBinSize',
                          {},
                          'Bin-størrelse mangler — legg til minst én bin-størrelse for varen'
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingData && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center space-y-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="text-gray-600">Henter data...</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Form Fields */}
          {!loadingData && (
            <>
              {/* Basic Configuration */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Grunnkonfigurasjon</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* BinGrpNo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lokasjonsgruppe
                    </label>
                    <select
                      value={binGrpNo ?? ''}
                      onChange={(e) => setBinGrpNo(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Velg lokasjonsgruppe</option>
                      {binGroups.map((bg) => (
                        <option key={bg.id} value={bg.binGrpNo}>
                          {bg.binGrpNo} - {bg.description1}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* PickRuleNo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plukkreg el
                    </label>
                    <select
                      value={pickRuleNo ?? ''}
                      onChange={(e) => setPickRuleNo(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Velg plukkreg el</option>
                      {pickRules.map((pr) => (
                        <option key={pr.pickRuleNo} value={pr.pickRuleNo}>
                          {pr.pickRuleName} - {pr.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* StcBoxTypeID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sorteringsbokstype
                    </label>
                    <select
                      value={stcBoxTypeID}
                      onChange={(e) => setStcBoxTypeID(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Velg sorteringsbokstype</option>
                      {sortingBoxTypes.map((sbt) => (
                        <option key={sbt.id} value={sbt.stcBoxTypeID}>
                          {sbt.stcBoxTypeID} - {sbt.description1 || 'Ingen beskrivelse'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* MovementTypeNo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bevegelseskode
                    </label>
                    <select
                      value={movementTypeNo ?? ''}
                      onChange={(e) => setMovementTypeNo(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Velg bevegelseskode</option>
                      {movementTypes.map((mt) => (
                        <option key={mt.id} value={mt.movementTypeNo}>
                          {mt.movementTypeNo} - {mt.description1}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* DedicatedBinCode with Suggest Button */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fastlokasjon, kun denne vare
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={dedicatedBinCode}
                        onChange={(e) => setDedicatedBinCode(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Angi lokasjon"
                      />
                      <button
                        type="button"
                        onClick={handleSuggestDedicatedBin}
                        disabled={loadingSuggestion || !binGrpNo}
                        className="px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={binGrpNo ? "Foreslå ledig lokasjon" : "Velg lokasjonsgruppe først"}
                      >
                        {loadingSuggestion ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Lightbulb className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* PutAwayToPickableBinYesNo */}
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={putAwayToPickableBinYesNo}
                        onChange={(e) => setPutAwayToPickableBinYesNo(e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Mottak til plukklokasjon
                      </span>
                    </label>
                  </div>

                  {/* UseSharedBinYesNo */}
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useSharedBinYesNo}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseSharedBinYesNo(checked);
                          // When operator turns off "Bruk felleslokasjon" the StdBinCode
                          // input is disabled — clear the value so we don't silently
                          // re-save a stale (and possibly invalid) bin code.
                          if (!checked) {
                            setStdBinCode('');
                          }
                        }}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Bruk felleslokasjon
                      </span>
                    </label>
                  </div>

                  {/* StdBinCode with Suggest Button - Only editable when UseSharedBinYesNo is true */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fast lokasjon (StdBinCode)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={stdBinCode}
                        onChange={(e) => setStdBinCode(e.target.value)}
                        disabled={!useSharedBinYesNo}
                        className={`flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          !useSharedBinYesNo ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                        }`}
                        placeholder={useSharedBinYesNo ? "Angi lokasjon" : "Aktiver 'Bruk felleslokasjon' for å sette fast lokasjon"}
                      />
                      <button
                        type="button"
                        onClick={handleSuggestBin}
                        disabled={loadingSuggestion || !binGrpNo}
                        className="px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        title={binGrpNo ? "Foreslå lokasjon" : "Velg lokasjonsgruppe først"}
                      >
                        {loadingSuggestion ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Lightbulb className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ItemBinSize Grid */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-900">Bin-størrelser</h3>
                  <button
                    onClick={handleAddBinSize}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Legg til bin-størrelse</span>
                  </button>
                </div>

                {itemBinSizes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    Ingen bin-størrelser definert. Klikk "Legg til bin-størrelse" for å legge til.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Bin-størrelse
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Maks antall
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            Plasseringsprioritet
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                            Handling
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {itemBinSizes.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            {/* BinSizeNo */}
                            <td className="px-3 py-2">
                              <select
                                value={row.binSizeNo ?? ''}
                                onChange={(e) =>
                                  handleBinSizeChange(
                                    row.id,
                                    'binSizeNo',
                                    e.target.value ? parseInt(e.target.value) : null
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="">Velg bin-størrelse</option>
                                {binSizes.map((bs) => (
                                  <option key={bs.id} value={bs.binSizeNo}>
                                    {bs.description1}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* MaxQuantity */}
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={row.maxQuantity}
                                onChange={(e) =>
                                  handleBinSizeChange(
                                    row.id,
                                    'maxQuantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                min="0"
                                step="1"
                              />
                            </td>

                            {/* PutAwayPriorityNo */}
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={row.putAwayPriorityNo}
                                onChange={(e) =>
                                  handleBinSizeChange(
                                    row.id,
                                    'putAwayPriorityNo',
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                min="0"
                                step="1"
                              />
                            </td>

                            {/* Delete Button */}
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handleRemoveBinSize(row.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Slett rad"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 sticky bottom-0 bg-white">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={loading || loadingData}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Lagrer...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Lagre</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
